import { stylesheet } from 'typestyle';
import { Alert, Gray100, typeCompact14RegularProperties } from '@traceair/tds';
import { ScheduledFlight } from '../../../../../types';
import {
  GeorefData,
  RegistrationRequest,
  RegistrationRequestType,
  UploadData,
  useUploadStore,
} from '../../../../../store/uploadStore';
import { useHandleFileAdd } from './useHandleFileAdd/useHandleFileAdd';
import { ProjectData } from '../../types';
import { useAllowedFileTypes } from './useAllowedFileTypes';
import { UploadType } from '../../../../../consts/uploadType';
import { FlightUploadProgress } from './uploadProgress/flightUploadProgress/FlightUploadProgress';
import { FlightUploadGeoref } from './FlightUploadGeoref';
import { FlightUploadQuality } from './flightUploadQuality/FlightUploadQuality';
import { FileUpload } from './FileUpload';

const css = stylesheet({
  heading: {
    ...typeCompact14RegularProperties,
    marginBottom: 8,
    color: Gray100,
  },
  error: {
    marginBottom: 8,
  },
  georefAlert: {
    marginBottom: 6,
  },
});

interface Props extends ProjectData {
  isPano: boolean;
  projectDate: string | undefined;
  registrationRequests: Map<Promise<unknown>, RegistrationRequest> | undefined;
  schedule?: ScheduledFlight;
  upload: UploadData | undefined;
  uploadType: UploadType;
}

export const FlightUploadFiles = ({
  isPano,
  customer,
  site,
  schedule,
  projectDate,
  registrationRequests,
  upload,
  uploadType,
}: Props) => {
  const { setUpload, addRegistrationRequest, removeRegistrationRequest, getGeoref, setGeoref } = useUploadStore();

  const georef = getGeoref(customer.id, site.id, schedule?.airtableRecordId);

  const wrappedSetUpload = (uploadData: UploadData | undefined) =>
    setUpload(isPano, customer.id, site.id, schedule?.airtableRecordId, uploadData);
  const wrappedSetGeoref = (georef: GeorefData) => setGeoref(customer.id, site.id, schedule?.airtableRecordId, georef);

  const wrappedAddRegistrationRequest = (promise: Promise<unknown>, registrationRequest: RegistrationRequest) =>
    addRegistrationRequest(isPano, customer.id, site.id, schedule?.airtableRecordId, promise, registrationRequest);

  const wrappedRemoveRegistrationRequest = (promise: Promise<unknown>) =>
    removeRegistrationRequest(isPano, customer.id, site.id, schedule?.airtableRecordId, promise);

  const handleFileAdd = useHandleFileAdd(
    uploadType,
    customer,
    site,
    schedule,
    upload,
    wrappedSetUpload,
    wrappedSetGeoref,
    georef,
    projectDate,
    registrationRequests,
    wrappedAddRegistrationRequest,
    wrappedRemoveRegistrationRequest,
  );

  const hasFiles = !!registrationRequests?.size || !!upload?.overview.photosCount.all;
  const isLidar = uploadType === UploadType.Lidar;

  const disableFileUpload =
    !site.hasUploadPermission ||
    Array.from(registrationRequests?.values() ?? []).some(({ type }) =>
      [RegistrationRequestType.dateConfirmation, RegistrationRequestType.uploadRegistration].includes(type),
    );

  const acceptFiles = useAllowedFileTypes(uploadType);

  // For PPK scans we will only allow to upload photos after providing a georef
  const hasGeorefError = uploadType === UploadType.Ppk && !!georef?.meta.error;
  const showFileUpload = uploadType !== UploadType.Ppk || (!!georef?.georef && !hasGeorefError);
  const georefCount = Object.values(georef?.georef ?? {}).length;
  // @TODO Also consider filenames, right now it only considers the count
  const georefMismatch =
    georefCount > 0 && upload?.meta.type === UploadType.Ppk && georefCount !== upload.overview.photosCount.all;

  return (
    <div>
      {upload?.meta.error && (
        <Alert
          className={css.error}
          type="warning-accent"
          title="Something went wrong"
          description={upload.meta.error}
        />
      )}

      {uploadType === UploadType.Ppk && (
        <FlightUploadGeoref
          customer={customer}
          site={site}
          schedule={schedule}
          hasGeorefError={hasGeorefError}
          upload={upload}
        />
      )}

      {uploadType === UploadType.Ppk && (showFileUpload || upload) && <div className={css.heading}>Scan photos</div>}

      {showFileUpload && (
        <>
          <FileUpload
            accept={acceptFiles}
            disabled={disableFileUpload}
            dragOverText="Drop files or folders to add files"
            fileFormatIcons={isLidar ? [' ', ' ', ' '] : ['JPG']}
            hideIcons={hasFiles}
            multiple={true}
            onChange={handleFileAdd}
            title={isLidar ? 'Drop files or folders here, or' : 'Drop photos or folders here, or'}
            description={isLidar ? 'Include both LiDAR data and photos' : 'Preferred format: JPG'}
          />
        </>
      )}

      {site.hasUploadPermission && (
        <>
          <FlightUploadProgress
            isPano={isPano}
            customer={customer}
            site={site}
            schedule={schedule}
            registrationRequests={registrationRequests}
            upload={upload}
          />

          {georefMismatch && (
            <Alert
              className={css.georefAlert}
              type="attention"
              title="Georeferencing count mismatch"
              description={`The number of selected photos doesn't match the number of photos in the georeferencing file: ${upload?.overview.photosCount.all} out of ${georefCount}`}
            />
          )}

          <FlightUploadQuality customer={customer} site={site} upload={upload} />
        </>
      )}
    </div>
  );
};
